import { useMutation } from '@tanstack/react-query'
import axios from 'axios'
import { CheckCircle2, Info } from 'lucide-react'
import posthog from 'posthog-js'
import { useState } from 'react'

import { Banner } from '@/components/ui/banner'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { formatAcceptedFormats } from '@/components/ui/dropzone'
import { FieldError } from '@/components/ui/field-error'
import { Input } from '@/components/ui/input'
import { MultipleDropzone } from '@/components/ui/multiple-dropzone'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { appHref } from '@/config/config'
import { DotEntryForm, trackDotEntered, trackMarketingCta } from '@/lib/analytics'
import { cn } from '@/lib/utils'
import { submitLargeFleetPacket, type VerifiedCarrier, verifyDotNumber } from '@/models/largeFleetPacket'
import { PacketChecklist } from './PacketChecklist'
import { PACKET_ACCEPT, PACKET_MAX_SIZE_MB } from './packetData'
import { TravelRangeField } from './TravelRangeField'

const DOT_REGEX = /^\d{1,8}$/
// Mirrors sanitize_dot_number: the endpoint strips non-digits, so 'DOT 330-0318' is valid input.
const toDigits = (value: string) => value.replace(/\D/g, '')
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

// Inputs and the dropzone default to a white ground, so every field carries the inversion explicitly.
const DARK_INPUT =
  'bg-ink-800 border-ink-700 text-ink-50 placeholder:text-ink-400 enabled:hover:data-hovered:not-focus-visible:border-ink-500 focus-visible:border-brand-400'
const LABEL = 'text-ink-200 text-sm font-semibold'
const HINT = 'text-ink-300 text-sm'
// Dropzone sizes via an inline style, so the field group's max-w-md (28rem) has to be passed as px.
const FIELD_MAX_WIDTH_PX = 448

function errorMessage(error: unknown, fallback: string): string {
  if (axios.isAxiosError(error) && error.response?.status === 400) {
    const data = error.response.data as Record<string, unknown> | undefined
    const detail = data?.dot_number ?? data?.detail
    if (typeof detail === 'string') return detail
    if (Array.isArray(detail) && typeof detail[0] === 'string') return detail[0]
  }
  return fallback
}

const STANDARD_APPLICATION_HREF = appHref('/application/start')

function SmallFleetBanner() {
  return (
    <Banner
      variant="black"
      small
      icon={<Info className="text-ink-300" />}
      className="bg-ink-800 border-ink-700 mt-2"
    >
      <div className="flex flex-col gap-3">
        <p>
          <strong>It looks like you have less than 5 trucks.</strong>
          <br />
          To get quotes from insurance providers that support businesses like yours, go to the standard
          insurance application.
        </p>
        <Button
          size="sm"
          href={STANDARD_APPLICATION_HREF}
          className="self-start"
          onClick={() =>
            trackMarketingCta('large_fleet_small_fleet_start_application', {
              destination: STANDARD_APPLICATION_HREF,
            })
          }
        >
          Start an application
        </Button>
      </div>
    </Banner>
  )
}

export function PacketSubmitForm({ onSubmitted }: { onSubmitted: (email: string) => void }) {
  const [fullName, setFullName] = useState('')
  const [email, setEmail] = useState('')
  const [dotNumber, setDotNumber] = useState('')
  const [carrier, setCarrier] = useState<VerifiedCarrier | undefined>(undefined)
  const [travelRange, setTravelRange] = useState<string[]>([])
  const [files, setFiles] = useState<File[]>([])
  const [wantsGroupCaptive, setWantsGroupCaptive] = useState(false)
  const [showErrors, setShowErrors] = useState(false)

  const verifyMutation = useMutation({
    mutationFn: verifyDotNumber,
    // Ignore a response that lost the race to a newer DOT.
    onSuccess: (verified, requestedDot) => {
      // Counted even when a newer DOT superseded it: the backend recorded this one too.
      trackDotEntered(verified.dotNumber, DotEntryForm.LARGE_FLEETS)
      if (requestedDot === toDigits(dotNumber)) setCarrier(verified)
    },
  })
  const submitMutation = useMutation({
    mutationFn: submitLargeFleetPacket,
    onSuccess: (_result, submission) => onSubmitted(submission.email),
  })

  const normalizedDot = toDigits(dotNumber)
  const dotValid = DOT_REGEX.test(normalizedDot)
  const emailValid = EMAIL_REGEX.test(email.trim())
  const nameValid = fullName.trim().length > 1
  const smallFleet = carrier?.smallFleet === true
  const uploadDisabled = smallFleet || submitMutation.isPending

  // Re-verify whenever the number changes, so a confirmed carrier can never belong to a stale DOT.
  function handleDotChange(value: string) {
    setDotNumber(value)
    setCarrier(undefined)
  }

  function handleVerify() {
    if (!dotValid || verifyMutation.isPending) return
    verifyMutation.mutate(normalizedDot)
  }

  function handleSubmit(event: React.FormEvent) {
    event.preventDefault()
    if (smallFleet) return
    if (!nameValid || !emailValid || !carrier || travelRange.length === 0 || files.length === 0) {
      setShowErrors(true)
      return
    }
    posthog.capture('insure_large_fleet_packet_submit_clicked', { dot_number: normalizedDot })
    submitMutation.mutate({
      fullName: fullName.trim(),
      email: email.trim(),
      dotNumber: normalizedDot,
      travelRange,
      files,
      wantsGroupCaptive,
    })
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-12">
      <div className="grid gap-12 lg:grid-cols-[minmax(0,1fr)_380px] lg:gap-20">
        <div className="flex flex-col gap-12">
          <div className="flex flex-col gap-4">
            <span className="text-brand-400 font-mono text-xs tracking-[0.14em] uppercase">
              For Large Fleets
            </span>
            <h2 className="h2 text-ink-50 max-w-2xl">
              Upload your fleet info,
              <br />
              and we&apos;ll be in touch
            </h2>
          </div>

          <div className="flex flex-col gap-8">
            <div className="flex max-w-md flex-col gap-6">
              <div className="flex flex-col gap-2">
                <label htmlFor="full_name" className={LABEL}>
                  Full name
                </label>
                <Input
                  id="full_name"
                  name="full_name"
                  className={DARK_INPUT}
                  value={fullName}
                  onChange={(event) => setFullName(event.target.value)}
                  placeholder="Jordan Reyes"
                />
                <FieldError onDark message={showErrors && !nameValid ? 'Enter your name.' : undefined} />
              </div>

              <div className="flex flex-col gap-2">
                <label htmlFor="email" className={LABEL}>
                  Email
                </label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  className={DARK_INPUT}
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder="you@yourfleet.com"
                />
                <FieldError
                  onDark
                  message={showErrors && !emailValid ? 'Enter an email we can reach you at.' : undefined}
                />
              </div>

              <div className="flex flex-col gap-2">
                <label htmlFor="dot_number" className={LABEL}>
                  DOT number
                </label>
                <div className="flex items-start gap-2">
                  <Input
                    id="dot_number"
                    name="dot_number"
                    inputMode="numeric"
                    className={DARK_INPUT}
                    value={dotNumber}
                    onChange={(event) => handleDotChange(event.target.value)}
                    placeholder="1234567"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    className="border-ink-600 text-ink-50 hover:bg-ink-800 hover:border-ink-500 shrink-0 bg-transparent"
                    onClick={handleVerify}
                    loading={verifyMutation.isPending ? 'Checking' : undefined}
                    disabled={!dotValid}
                  >
                    Check
                  </Button>
                </div>
                {carrier && (
                  <div className="text-ink-50 flex items-start gap-2 text-sm">
                    <CheckCircle2 className="text-brand-400 mt-0.5 size-4 shrink-0" />
                    <span>
                      <span className="font-semibold">{carrier.legalName}</span>
                      {carrier.city && carrier.state && (
                        <span className="text-ink-300">
                          {' '}
                          — {carrier.city}, {carrier.state}
                        </span>
                      )}
                    </span>
                  </div>
                )}
                {smallFleet && <SmallFleetBanner />}
                <FieldError
                  onDark
                  message={
                    verifyMutation.isError && verifyMutation.variables === normalizedDot
                      ? errorMessage(verifyMutation.error, 'We could not look that DOT number up. Try again.')
                      : showErrors && !carrier
                        ? 'Check your DOT number so we know which fleet this is.'
                        : undefined
                  }
                />
              </div>
            </div>

            <div className="flex max-w-2xl flex-col gap-3">
              <div className="flex items-baseline gap-2">
                <span className={LABEL}>Typical travel range</span>
                <span className={HINT}>Select all that apply</span>
              </div>
              <TravelRangeField selected={travelRange} onChange={setTravelRange} disabled={smallFleet} />
              <FieldError
                onDark
                message={
                  showErrors && travelRange.length === 0
                    ? 'Pick the ranges your trucks usually run.'
                    : undefined
                }
              />
            </div>
          </div>

          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-1.5">
                <span className={LABEL}>Your paperwork</span>
                <Tooltip>
                  <TooltipTrigger asChild>
                    {/* type=button: Button sets none, and an unset type submits the form. */}
                    <Button
                      type="button"
                      variant="iconOnly"
                      size="icon-sm"
                      aria-label="Accepted file types and size limit"
                      className="text-ink-400 hover:text-ink-200 size-5"
                    >
                      <Info className="size-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    Max. size: {PACKET_MAX_SIZE_MB} MB
                    <br />
                    Accepted file: {formatAcceptedFormats(PACKET_ACCEPT)}
                  </TooltipContent>
                </Tooltip>
              </div>
              <p className={HINT}>Incomplete information is ok.</p>
            </div>
            <MultipleDropzone
              onChange={setFiles}
              accept={PACKET_ACCEPT}
              maxWidth={FIELD_MAX_WIDTH_PX}
              maxSizeMB={PACKET_MAX_SIZE_MB}
              showFileHints={false}
              onDark
              disabled={uploadDisabled}
              className="text-ink-200"
              dropzoneClassName={cn(
                'bg-ink-800 border-ink-600 mx-0 min-h-[136px]',
                !uploadDisabled && 'hover:border-ink-500 hover:bg-ink-700'
              )}
            />
            <FieldError
              onDark
              message={
                showErrors && files.length === 0
                  ? 'Add at least one file so your broker can get started shopping for quotes.'
                  : undefined
              }
            />
          </div>

          <FieldError
            onDark
            message={
              submitMutation.isError
                ? errorMessage(submitMutation.error, 'Something went wrong sending your files. Try again.')
                : undefined
            }
          />
        </div>
        <PacketChecklist />
      </div>

      <div className="border-ink-700 flex flex-col gap-3 border-t pt-8">
        <div className="flex items-center gap-2 pb-3">
          <Checkbox
            id="group_captive"
            checked={wantsGroupCaptive}
            onCheckedChange={(checked) => setWantsGroupCaptive(checked === true)}
            disabled={smallFleet}
            className="border-ink-500"
          />
          <label htmlFor="group_captive" className="text-ink-200 text-sm">
            Contact me about group captive options for my fleet.
          </label>
        </div>
        <Button
          type="submit"
          size="xlg"
          loading={submitMutation.isPending ? 'Sending' : undefined}
          disabled={smallFleet}
          className="text-md self-start"
        >
          Get quotes
        </Button>
        <p className="text-ink-300 text-sm">No cost, no obligation to buy. We reply within 1 business day.</p>
      </div>
    </form>
  )
}
